<template>
  <article class="pricing">
    <div class="pricing__inner">
      <div class="grid grid--container">
        <div class="row row--md-h-center">
          <div class="col col--lg-8 col--md-10">
            <div class="page-intro">
              <h1>Campsite.bio Plans &amp; Pricing</h1>
              <p>
                Whether you're a creator, solo-entrepreneur, small business, or
                agency, we've got you covered. Start our Pro plan with a 10-day
                trial today and cancel anytime.
              </p>
            </div>
          </div>

          <div class="col col--xl-10 col--lg-11">
            <Plans />

            <div class="pricing__compare-link">
              <InlineLink href="#compare"
                ><span
                  >Compare our plans features <ChevronIcon size=".8em" /></span
              ></InlineLink>
            </div>
          </div>

          <div class="col col--xl-8 col--lg-10">
            <Card class="inset" :hideStaticShadow="false" color="gray100">
              <CardText>
                <h2 class="h3">Discount for Non-profits</h2>
                <p>
                  At Campsite, we're proud to support teams doing great things.
                  Reach out at
                  <a href="mailto:support@campsite.bio">support@campsite.bio</a>
                  to apply for a discount on any Pro plan. We'll need proof of
                  non-profit status, like an IRS determination letter.
                </p>
              </CardText>
            </Card>
          </div>

          <div class="col col--xl-10 col--lg-11">
            <PricingCompare id="compare" />
          </div>
        </div>
      </div>

      <section class="pricing__faq">
        <div class="grid grid--container">
          <h2>Frequently Asked Questions</h2>
          <div class="row row--md-h-center">
            <div class="col col--lg-8">
              <AccordionItem title="What if I need more profiles?">
                <p>
                  We support plan modifications so you can add more profiles
                  when you need them. It costs $1.50 per month or $15 per year
                  to add more profiles.
                </p>
              </AccordionItem>
              <AccordionItem title="What is a custom domain?">
                <p>
                  A Custom Domain is a domain name (www.mybrand.com) you
                  independently buy from a 3rd party domain registrar. They cost
                  on average $10-$15 a year, depending on the seller and
                  estimated domain name value.
                </p>
                <p>
                  You can use your purchased custom domain on Campsite and share
                  it with your followers (ex. links.mybrand.com). This is
                  powerful in that it allows you to present a link that your
                  followers will be familiar with and trust.
                </p>
              </AccordionItem>
              <AccordionItem
                title="What sort of analytics do I get with Pro and Pro+?"
              >
                <h3>Pro</h3>
                <p>
                  Campsite.bio Pro unlocks access to detailed analytics like
                  page views and clicks breakdowns, along with referral,
                  country, device, and event charts. You can view up to 60 days
                  of data with Pro.
                </p>
                <p>
                  For your links, you can view a day-by-day breakdown of clicks,
                  for up to 28 days. You'll also be able to see top referrers,
                  countries, and device types.
                </p>

                <h3>Pro+ / Analytics add-on</h3>
                <p>
                  Our Analytics add-on, which is available to be added to the
                  Pro subscription, or built into the Pro+ subscription, unlocks
                  even more insights. Get lifetime analytics storage, five
                  different reports, data filtering capabilities, and the
                  ability to download your analytics to a .csv.
                </p>
              </AccordionItem>
              <AccordionItem
                title="How does someone signup for our email list?"
              >
                <p>
                  Campsite Pro has seven integrations for email signups,
                  ActiveCampaign, ConvertKit, Mailchimp, MailerLite, Klaviyo,
                  Omnisend and Google Sheets. When you add a new link, you're
                  able to add what we call an
                  <nuxt-link
                    to="https://support.campsite.bio/en/articles/6840703-opt-in-form-link"
                    >"Opt-in Form" link</nuxt-link
                  >.
                </p>

                <p>
                  When a user visits your profile and clicks on this new link,
                  they can enter their email or phone number, which will be
                  added to your selected integration.
                </p>
              </AccordionItem>
              <AccordionItem title="What is Campsite.bio?">
                <p>
                  Campsite.bio started as a solution to the one link problem on
                  Instagram (commonly referred to as your bio link or website).
                  Think of Campsite.bio as a landing page for your brand or
                  website. Campsite.bio can be used on any social platform where
                  you want to share all your links.
                </p>
              </AccordionItem>
              <p style="margin-top: 1.875rem">
                Don't see an answer to your question?
                <nuxt-link
                  to="https://support.campsite.bio/en/collections/3754648-faqs"
                  >Check out our FAQ page</nuxt-link
                >.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <PromoSection
      title="Get a blazing fast bio link with Campsite.bio"
      text="Use our feature rich tool for free today"
    >
      <Button
        href="https://app.campsite.bio/create-account"
        size="large"
        color="secondary"
        variant="hard-shadow"
        >Get Started</Button
      >
    </PromoSection>
  </article>
</template>

<script>
import {
  InlineLink,
  ChevronIcon,
  Card,
  CardText,
} from '@campsite-bio/component-lib';
import { generateMeta } from '@/utils/generate-meta';
import PromoSection from '@/components/promo-section';
import AccordionItem from '@/components/accordion-item';
import Plans from '@/components/pricing/plans';
import PricingCompare from '@/components/pricing/pricing-compare';
import { Button } from '@campsite-bio/component-lib';

export default {
  head() {
    return generateMeta({
      title: 'Campsite.bio Pricing and Features - Free or just $7 a month',
      description:
        'Compare our free forever plan with our paid plan for Brands, Agencies, and Marketing Teams. Upgrade your bio link now.',
    });
  },

  components: {
    InlineLink,
    ChevronIcon,
    PromoSection,
    AccordionItem,
    Plans,
    PricingCompare,
    Button,
    Card,
    CardText,
  },
};
</script>

<style lang="scss" scoped>
.pricing {
  background: #f7f7f7;
  height: 100%;

  .pyro {
    left: 50%;
    pointer-events: none;
    position: fixed;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.pricing__inner {
  padding: 1rem 0 3.75rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 2.5rem 0 3.75rem;
  }
}

.pricing__compare-link {
  margin-top: 2rem;
}

.setup-section {
  margin-top: 3.125rem;
}

.pricing__faq {
  border-top: 1px solid #cfd5e1;
  padding-top: 2.5rem;
  margin-top: 2.375rem;

  @media (min-width: 768px) {
    margin-top: 4.375rem;
  }
}

.inset {
  margin: 2rem 0;
}
</style>
